import { TabProxy } from "encampy";
import { useCallback, useEffect, useState } from "react";
import { EncampyLaunchData } from "../components/Encampy/AutomationLauncher";
import { createAutomationPlan } from "../util/encampy/automationHelpers";

interface UseEncampyLaunchDataReturn {
  launchData: EncampyLaunchData | null;
  isLoading: boolean;
  error: string | null;
}

export function useEncampyLaunchData(
  url: string | null | undefined,
  automate: ((tab: TabProxy) => Promise<void>) | null,
  dependencies: any[]
): UseEncampyLaunchDataReturn {
  const [launchData, setLaunchData] = useState<EncampyLaunchData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stableAutomate = useCallback(
    automate || (async () => {}),
    dependencies
  );

  useEffect(() => {
    async function generateLaunchData() {
      if (!url) {
        setLaunchData(null);
        setError("URL is required");
        return;
      }

      if (!stableAutomate) {
        setLaunchData(null);
        setError("Automation function is required");
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const tasks = await createAutomationPlan(stableAutomate);
        setLaunchData({
          url,
          tasks,
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to generate launch data";
        setError(errorMessage);
        setLaunchData(null);
      } finally {
        setIsLoading(false);
      }
    }

    generateLaunchData();
  }, [url, stableAutomate]);

  return {
    launchData,
    isLoading,
    error,
  };
}
